import axios from '@/config/axios';
import { useToast } from 'vue-toastification';

// Import API của các entity liên quan (mỗi file tự định nghĩa API của nó)
import { authorApi } from '../authors/authors';
import { translatorApi } from '../translators/translators';
import { categoryApi } from '../categories/categories';

// ═══════════════════════════════════════════════════════════════════════════
// API — /book/v2/...
// Sửa endpoint tại đây khi backend thay đổi.
// ═══════════════════════════════════════════════════════════════════════════
function unwrap(res) {
  const { status, message, result } = res.data;
  if (status !== '200') throw new Error(message || 'Có lỗi xảy ra từ máy chủ.');
  return result;
}

export const bookApi = {
  /** GET /book/v2/get-list — lấy danh sách sách (phân trang + bộ lọc) */
  getList: (page = 0, filters = {}) => {
    const params = { page };
    if (filters.search && filters.search.trim()) params.key = filters.search.trim();
    if (filters.authorId) params.authorId = filters.authorId;
    if (filters.translatorId) params.translatorId = filters.translatorId;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.status !== undefined && filters.status !== 'ALL') params.status = parseInt(filters.status, 10);
    if (filters.hasCover !== undefined && filters.hasCover !== 'ALL') params.hasCover = parseInt(filters.hasCover, 10);
    return axios.get('book/v2/get-list', { params }).then(unwrap);
  },

  /** GET /book/v2/search — tìm kiếm sách (phân trang + bộ lọc) */
  search: (key = '', page = 0, filters = {}) => {
    const params = { page };
    if (key && key.trim()) params.key = key.trim();
    if (filters.authorId) params.authorId = filters.authorId;
    if (filters.translatorId) params.translatorId = filters.translatorId;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.status !== undefined && filters.status !== 'ALL') params.status = parseInt(filters.status, 10);
    if (filters.hasCover !== undefined && filters.hasCover !== 'ALL') params.hasCover = parseInt(filters.hasCover, 10);
    return axios.get('book/v2/search', { params }).then(unwrap);
  },

  /**
   * POST /book/v2/create — tạo sách mới.
   * Backend nhận multipart/form-data:
   *   - cover: file ảnh bìa
   *   - data:  chuỗi JSON của BookCreateDto
   *            { name, content, linkShopee, authors[], categories[], translators[] }
   * (không tự set Content-Type để trình duyệt tự thêm boundary cho multipart)
   */
  create: (formData) =>
    axios.post('book/v2/create', formData).then(unwrap),

  update: (formData) =>
    axios.post('book/v2/update', formData).then(unwrap),

  remove: (payload) =>
    axios.post('book/v2/delete', payload).then(unwrap),

  restore: (payload) =>
    axios.post('book/v2/restore', payload).then(unwrap),

  getHistory: (id) =>
    axios.get(`history/v2/book/get-by-id/${id}`).then(res => {
      const { status, message, result } = res.data;
      if (status === '404') return [];
      if (status !== '200') throw new Error(message || 'Có lỗi xảy ra từ máy chủ.');
      return result;
    }),

};
// ═══════════════════════════════════════════════════════════════════════════

const toast = {
  success(msg, options = {}) {
    return useToast().success(msg, { position: "top-center", ...options });
  },
  error(msg, options = {}) {
    return useToast().error(msg, { position: "top-center", ...options });
  },
  warning(msg, options = {}) {
    return useToast().warning(msg, { position: "top-center", ...options });
  }
};

function removeAccents(str) {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd');
}

export default {
  name: 'AdminBooks',
  data() {
    return {
      books: [],
      authors: [],
      translators: [],
      categories: [],
      isLoading: false,
      filters: {
        search: '',
        authorId: '',
        translatorId: '',
        categoryId: '',
        status: 'ALL',
        hasCover: 'ALL'
      },
      modalVisible: false,
      isEditMode: false,
      viewModalVisible: false,
      imagePreviewVisible: false,
      imageLoadError: false,
      deleteModalVisible: false,
      deleteReason: '',
      restoreModalVisible: false,
      selectedBookForRestore: null,
      restoreReason: '',
      reasonModalVisible: false,
      selectedBook: null,
      form: {
        id: null,
        name: '',
        content: '',
        cover: '',
        coverFile: null,
        status: 0,
        link_shopee: '',
        authorIds: [],
        translatorIds: [],
        categoryIds: [],
        reason: '',
        updateTime: ''
      },
      errors: { name: '', content: '', authors: '', categories: '', reason: '', deleteReason: '', restoreReason: '' },
      searchAuthor: '',
      searchTranslator: '',
      searchCategory: '',
      activeDropdown: '',
      searchQueryAuthor: '',
      searchQueryTranslator: '',
      searchQueryCategory: '',
      searchQueryStatus: '',
      historyModalVisible: false,
      dragOver: false,
      currentPage: 0,
      expandedStates: {},
      totalPages: 1,
      totalElements: 0,
      pageSize: 10,
      searchTimeout: null,
      hasLoadedAuthors: false,
      hasLoadedTranslators: false,
      hasLoadedCategories: false,
      defaultAuthors: [],
      defaultTranslators: [],
      defaultCategories: [],
      formAuthors: [],
      formTranslators: [],
      formCategories: [],
      loadedAuthors: {},
      loadedAuthorNames: {},
      loadedTranslators: {},
      loadedCategories: {},
      searchAuthorTimeout: null,
      searchTranslatorTimeout: null,
      searchCategoryTimeout: null,
      searchModalAuthorTimeout: null,
      searchModalTranslatorTimeout: null,
      searchModalCategoryTimeout: null,
      coverOptions: [
        { value: 'ALL', label: 'Tất cả ảnh bìa' },
        { value: 1, label: 'Đã có ảnh' },
        { value: 0, label: 'Chưa có ảnh' }
      ]
    };
  },
  computed: {
    filteredBooks() {
      return this.books;
    },
    sortedAuthors() {
      let list = [...this.authors];
      list = list.filter(a => !this.form.authorIds.includes(a.id));
      const rawQ = this.searchAuthor.trim().toLowerCase();
      if (rawQ) {
        const cleanQ = removeAccents(rawQ);
        list = list.filter(a => {
          const n = a.name.toLowerCase();
          return n.includes(rawQ) || removeAccents(n).includes(cleanQ);
        });
      }
      return list.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    },
    sortedTranslators() {
      let list = [...this.translators];
      list = list.filter(t => !this.form.translatorIds.includes(t.id));
      const rawQ = this.searchTranslator.trim().toLowerCase();
      if (rawQ) {
        const cleanQ = removeAccents(rawQ);
        list = list.filter(t => {
          const n = t.name.toLowerCase();
          return n.includes(rawQ) || removeAccents(n).includes(cleanQ);
        });
      }
      return list.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    },
    sortedCategories() {
      let list = [...this.categories];
      list = list.filter(c => !this.form.categoryIds.includes(c.id));
      const rawQ = this.searchCategory.trim().toLowerCase();
      if (rawQ) {
        const cleanQ = removeAccents(rawQ);
        list = list.filter(c => {
          const n = c.name.toLowerCase();
          return n.includes(rawQ) || removeAccents(n).includes(cleanQ);
        });
      }
      return list.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    },
    currentAuthorLabel() {
      return this.loadedAuthors[this.filters.authorId] || 'Tất cả tác giả';
    },
    currentTranslatorLabel() {
      return this.loadedTranslators[this.filters.translatorId] || 'Tất cả dịch giả';
    },
    currentCategoryLabel() {
      return this.loadedCategories[this.filters.categoryId] || 'Tất cả thể loại';
    },
    currentStatusLabel() {
      if (this.filters.status === 'ALL') return 'Tất cả trạng thái';
      if (this.filters.status === '0') return 'Phát hành';
      if (this.filters.status === '1') return 'Chờ xóa';
      if (this.filters.status === '2') return 'Đã xóa';
      if (this.filters.status === '3') return 'Chờ khôi phục';
      return 'Tất cả trạng thái';
    },
    currentCoverLabel() {
      const opt = this.coverOptions.find(o => o.value === this.filters.hasCover);
      return opt ? opt.label : 'Tất cả ảnh bìa';
    },
    activeFilterCount() {
      let count = 0;
      if (this.filters.search && this.filters.search.trim()) count++;
      if (this.filters.authorId) count++;
      if (this.filters.translatorId) count++;
      if (this.filters.categoryId) count++;
      if (this.filters.status !== undefined && this.filters.status !== 'ALL') count++;
      if (this.filters.hasCover !== undefined && this.filters.hasCover !== 'ALL') count++;
      return count;
    },
    activeFilterTags() {
      const tags = [];
      if (this.filters.search && this.filters.search.trim()) {
        tags.push({ key: 'search', label: `Từ khóa: "${this.filters.search.trim()}"` });
      }
      if (this.filters.authorId) {
        tags.push({ key: 'authorId', label: `Tác giả: ${this.currentAuthorLabel}` });
      }
      if (this.filters.translatorId) {
        tags.push({ key: 'translatorId', label: `Dịch giả: ${this.currentTranslatorLabel}` });
      }
      if (this.filters.categoryId) {
        tags.push({ key: 'categoryId', label: `Thể loại: ${this.currentCategoryLabel}` });
      }
      if (this.filters.status !== undefined && this.filters.status !== 'ALL') {
        tags.push({ key: 'status', label: `Trạng thái: ${this.currentStatusLabel}` });
      }
      if (this.filters.hasCover !== undefined && this.filters.hasCover !== 'ALL') {
        tags.push({ key: 'hasCover', label: `Ảnh bìa: ${this.currentCoverLabel}` });
      }
      return tags;
    },
    filteredDropdownAuthors() {
      const q = removeAccents(this.searchQueryAuthor.trim().toLowerCase());
      return q ? this.authors.filter(a => removeAccents(a.name.toLowerCase()).includes(q)) : this.authors;
    },
    filteredDropdownTranslators() {
      const q = removeAccents(this.searchQueryTranslator.trim().toLowerCase());
      return q ? this.translators.filter(t => removeAccents(t.name.toLowerCase()).includes(q)) : this.translators;
    },
    filteredDropdownCategories() {
      const q = removeAccents(this.searchQueryCategory.trim().toLowerCase());
      return q ? this.categories.filter(c => removeAccents(c.name.toLowerCase()).includes(q)) : this.categories;
    },
    filteredDropdownStatuses() {
      const options = [
        { value: 'ALL', label: 'Tất cả trạng thái' },
        { value: '0', label: 'Phát hành' },
        { value: '1', label: 'Chờ xóa' },
        { value: '3', label: 'Chờ khôi phục' },
        { value: '2', label: 'Đã xóa' }
      ];
      const q = removeAccents(this.searchQueryStatus.trim().toLowerCase());
      return q ? options.filter(o => removeAccents(o.label.toLowerCase()).includes(q)) : options;
    },
    isAdmin() {
      try {
        const accStr = localStorage.getItem('current-account');
        if (!accStr) return true;
        const acc = JSON.parse(accStr);
        const authRaw = acc.authority;
        const authList = typeof authRaw === 'string' ? JSON.parse(authRaw) : authRaw;
        if (Array.isArray(authList)) {
          return authList.some(a => {
            const role = String(a.authority || a).toUpperCase();
            return role === 'ROLE_ADMIN' || role === 'ADMIN';
          });
        }
        return true;
      } catch (e) {
        return true;
      }
    },
    deleteModalTitle() {
      return this.isAdmin ? 'Xác nhận xóa sách' : 'Xác nhận gửi yêu cầu xóa sách';
    },
    deleteConfirmText() {
      return this.isAdmin
        ? `Bạn có chắc chắn muốn xóa cuốn sách <strong>${this.selectedBook?.name || ''}</strong> khỏi kho hệ thống?`
        : `Bạn có chắc chắn muốn gửi yêu cầu xóa cuốn sách <strong>${this.selectedBook?.name || ''}</strong>?`;
    },
    deleteWarningText() {
      return this.isAdmin
        ? 'Toàn bộ dữ liệu cuốn sách sẽ được chuyển sang trạng thái đã xóa.'
        : 'Yêu cầu xóa sẽ được chuyển đến Admin để xem xét phê duyệt.';
    },
    deleteButtonText() {
      return this.isAdmin ? 'Xác nhận Xóa' : 'Gửi yêu cầu Xóa';
    },
    restoreModalTitle() {
      return this.isAdmin ? 'Khôi phục sách' : 'Yêu cầu khôi phục sách';
    },
    restoreConfirmText() {
      return this.isAdmin
        ? `Bạn có chắc chắn muốn khôi phục cuốn sách <strong>${this.selectedBookForRestore?.name || ''}</strong>?`
        : `Bạn muốn gửi yêu cầu khôi phục cuốn sách <strong>${this.selectedBookForRestore?.name || ''}</strong>?`;
    },
    restoreWarningText() {
      return this.isAdmin
        ? 'Cuốn sách sẽ được chuyển về trạng thái Đang phát hành.'
        : 'Yêu cầu khôi phục sẽ được chuyển đến Quản trị viên để xét duyệt.';
    },
    restoreButtonText() {
      return this.isAdmin ? 'Khôi phục' : 'Gửi yêu cầu';
    },
    visiblePages() {
      const range = [];
      const maxVisible = 5;
      let start = Math.max(0, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible);

      if (end - start < maxVisible) {
        start = Math.max(0, end - maxVisible);
      }

      for (let i = start; i < end; i++) {
        range.push(i);
      }
      return range;
    },
    isUnchanged() {
      if (!this.isEditMode) return false;
      const book = this.books.find(b => b.id === this.form.id);
      if (!book) return false;

      // Compare basic fields
      if (this.form.name.trim() !== (book.name || '').trim()) return false;
      if (this.form.content.trim() !== (book.content || '').trim()) return false;
      if (this.form.cover !== (book.cover || '')) return false;
      if (this.form.status !== book.status) return false;
      if (this.form.link_shopee.trim() !== (book.link_shopee || '').trim()) return false;

      // Compare arrays
      const compareArrays = (arr1, arr2) => {
        const a1 = arr1 || [];
        const a2 = arr2 || [];
        if (a1.length !== a2.length) return false;
        const s1 = [...a1].sort();
        const s2 = [...a2].sort();
        return s1.every((v, i) => v === s2[i]);
      };

      if (!compareArrays(this.form.authorIds, book.authorIds)) return false;
      if (!compareArrays(this.form.translatorIds, book.translatorIds)) return false;
      if (!compareArrays(this.form.categoryIds, book.categoryIds)) return false;

      return true;
    }
  },
  watch: {

    searchQueryAuthor(newVal) {
      if (this.searchAuthorTimeout) clearTimeout(this.searchAuthorTimeout);
      this.searchAuthorTimeout = setTimeout(() => {
        this.fetchRemoteAuthors(newVal);
      }, 300);
    },
    searchQueryTranslator(newVal) {
      if (this.searchTranslatorTimeout) clearTimeout(this.searchTranslatorTimeout);
      this.searchTranslatorTimeout = setTimeout(() => {
        this.fetchRemoteTranslators(newVal);
      }, 300);
    },
    searchQueryCategory(newVal) {
      if (this.searchCategoryTimeout) clearTimeout(this.searchCategoryTimeout);
      this.searchCategoryTimeout = setTimeout(() => {
        this.fetchRemoteCategories(newVal);
      }, 300);
    },
    searchAuthor(newVal) {
      if (this.searchModalAuthorTimeout) clearTimeout(this.searchModalAuthorTimeout);
      this.searchModalAuthorTimeout = setTimeout(() => {
        this.fetchRemoteAuthors(newVal);
      }, 300);
    },
    searchTranslator(newVal) {
      if (this.searchModalTranslatorTimeout) clearTimeout(this.searchModalTranslatorTimeout);
      this.searchModalTranslatorTimeout = setTimeout(() => {
        this.fetchRemoteTranslators(newVal);
      }, 300);
    },
    searchCategory(newVal) {
      if (this.searchModalCategoryTimeout) clearTimeout(this.searchModalCategoryTimeout);
      this.searchModalCategoryTimeout = setTimeout(() => {
        this.fetchRemoteCategories(newVal);
      }, 300);
    }
  },
  mounted() {
    this.reloadData();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    // ── SEARCH ─────────────────────────────────────────────────────────────
    /** Được gọi từ @input (native) — bypass IME composition guard của Vue */
    onSearchInput(val) {
      this.filters.search = val;
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.reloadData(0), 800);
    },
    searchNow() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.reloadData(0);
    },

    // ── DROPDOWN ───────────────────────────────────────────────────────────
    toggleDropdown(name) {
      if (this.activeDropdown === name) {
        this.activeDropdown = '';
      } else {
        this.activeDropdown = name;
        this.searchQueryAuthor = this.searchQueryTranslator = this.searchQueryCategory = this.searchQueryStatus = '';

        if (name === 'author' && !this.hasLoadedAuthors) {
          this.fetchDropdownAuthors();
        } else if (name === 'translator' && !this.hasLoadedTranslators) {
          this.fetchDropdownTranslators();
        } else if (name === 'category' && !this.hasLoadedCategories) {
          this.fetchDropdownCategories();
        }

        this.$nextTick(() => {
          if (name === 'author') this.$refs.searchAuthorInput?.focus();
          if (name === 'translator') this.$refs.searchTranslatorInput?.focus();
          if (name === 'category') this.$refs.searchCategoryInput?.focus();
          if (name === 'status') this.$refs.searchStatusInput?.focus();
        });
      }
    },
    async fetchDropdownAuthors() {
      try {
        const res = await authorApi.getList(0, 0);
        const list = (res.content ?? res ?? []);
        this.registerAuthors(list);
        this.defaultAuthors = list;
        this.authors = [...list];
        this.hasLoadedAuthors = true;
      } catch (err) {
        console.warn('Failed to load authors:', err);
      }
    },
    async fetchDropdownTranslators() {
      try {
        const res = await translatorApi.getList(0, 0);
        const list = (res.content ?? res ?? []);
        this.registerTranslators(list);
        this.defaultTranslators = list;
        this.translators = [...list];
        this.hasLoadedTranslators = true;
      } catch (err) {
        console.warn('Failed to load translators:', err);
      }
    },
    async fetchDropdownCategories() {
      try {
        const res = await categoryApi.getList(0, 0);
        const list = (res.content ?? res ?? []);
        this.registerCategories(list);
        this.defaultCategories = list;
        this.categories = [...list];
        this.hasLoadedCategories = true;
      } catch (err) {
        console.warn('Failed to load categories:', err);
      }
    },
    selectAuthor(id) { this.filters.authorId = id; this.activeDropdown = ''; this.reloadData(0); },
    selectTranslator(id) { this.filters.translatorId = id; this.activeDropdown = ''; this.reloadData(0); },
    selectCategory(id) { this.filters.categoryId = id; this.activeDropdown = ''; this.reloadData(0); },
    selectStatus(val) { this.filters.status = val; this.activeDropdown = ''; this.reloadData(0); },
    selectCover(val) { this.filters.hasCover = val; this.activeDropdown = ''; this.reloadData(0); },
    clearSingleFilter(key) {
      if (key === 'search') this.filters.search = '';
      else if (key === 'authorId') this.filters.authorId = '';
      else if (key === 'translatorId') this.filters.translatorId = '';
      else if (key === 'categoryId') this.filters.categoryId = '';
      else if (key === 'status') this.filters.status = 'ALL';
      else if (key === 'hasCover') this.filters.hasCover = 'ALL';
      this.reloadData(0);
    },
    clearAllFilters() {
      this.filters.search = '';
      this.filters.authorId = '';
      this.filters.translatorId = '';
      this.filters.categoryId = '';
      this.filters.status = 'ALL';
      this.filters.hasCover = 'ALL';
      this.reloadData(0);
    },
    handleDocumentClick(e) {
      if (this.activeDropdown && !e.target.closest('.filter-custom-dropdown')) {
        this.activeDropdown = '';
      }
    },

    // ── DATA ───────────────────────────────────────────────────────────────
    reloadData(page = 0) {
      this.isLoading = true;
      this.currentPage = page;

      bookApi.getList(page, this.filters)
        .then((bookResult) => {
          this.books = bookResult.content || bookResult || [];
          this.totalPages = bookResult.totalPages || 1;
          this.totalElements = bookResult.totalElements || this.books.length;
          this.pageSize = bookResult.size || bookResult.pageable?.pageSize || 10;

          this.books.forEach(b => {
            this.registerAuthors(b.authors);
            this.registerTranslators(b.translators);
            this.registerCategories(b.categories);
          });
        })
        .catch(err => toast.error(err.message || 'Có lỗi xảy ra khi tải dữ liệu sách.'))
        .finally(() => { this.isLoading = false; });
    },

    reloadBooks(page = 0) {
      return this.reloadData(page);
    },

    changePage(page) {
      if (page < 0 || page >= this.totalPages) return;
      this.reloadData(page);
    },

    // ── MODAL ──────────────────────────────────────────────────────────────
    openAddModal() {
      this.isEditMode = false;
      this.form = {
        id: null, name: '', content: '', cover: '', coverFile: null,
        status: 0, link_shopee: '',
        authorIds: [], translatorIds: [], categoryIds: [],
        reason: '', updateTime: ''
      };
      this.formAuthors = [];
      this.formTranslators = [];
      this.formCategories = [];
      this.errors = { name: '', content: '', authors: '', categories: '', reason: '', deleteReason: '' };
      this.searchAuthor = this.searchTranslator = this.searchCategory = '';
      
      if (!this.hasLoadedAuthors) this.fetchDropdownAuthors();
      if (!this.hasLoadedTranslators) this.fetchDropdownTranslators();
      if (!this.hasLoadedCategories) this.fetchDropdownCategories();

      this.modalVisible = true;
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },

    openEditModal(book) {
      this.isEditMode = true;
      this.form = {
        id: book.id,
        name: book.name,
        content: book.content || '',
        cover: book.cover || '',
        coverFile: null,
        status: book.status,
        link_shopee: book.link_shopee || '',
        authorIds: [...(book.authorIds || [])],
        translatorIds: [...(book.translatorIds || [])],
        categoryIds: [...(book.categoryIds || [])],
        reason: '',
        updateTime: book.update_time || ''
      };
      this.formAuthors = [...(book.authors || [])];
      this.formTranslators = [...(book.translators || [])];
      this.formCategories = [...(book.categories || [])];
      this.registerAuthors(this.formAuthors);
      this.registerTranslators(this.formTranslators);
      this.registerCategories(this.formCategories);
      this.errors = { name: '', content: '', authors: '', categories: '' };
      this.searchAuthor = this.searchTranslator = this.searchCategory = '';

      if (!this.hasLoadedAuthors) this.fetchDropdownAuthors();
      if (!this.hasLoadedTranslators) this.fetchDropdownTranslators();
      if (!this.hasLoadedCategories) this.fetchDropdownCategories();

      this.modalVisible = true;
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },

    closeModal() { this.modalVisible = false; },

    // ── VIEW MODAL ─────────────────────────────────────────────────────────
    openViewModal(book) { this.selectedBook = book; this.viewModalVisible = true; this.imageLoadError = false; },
    closeViewModal() { this.viewModalVisible = false; this.selectedBook = null; this.imageLoadError = false; },
    editFromView() {
      const book = this.selectedBook;
      this.closeViewModal();
      this.$nextTick(() => this.openEditModal(book));
    },
    openImagePreview() { this.imagePreviewVisible = true; },
    closeImagePreview() { this.imagePreviewVisible = false; },

    // ── VALIDATION ─────────────────────────────────────────────────────────
    validateForm() {
      let isValid = true;
      this.errors = { name: '', content: '', authors: '', categories: '' };

      if (!this.form.name.trim()) {
        this.errors.name = 'Tên sách không được bỏ trống.'; isValid = false;
      }
      if (!this.form.content.trim()) {
        this.errors.content = 'Tóm tắt nội dung không được bỏ trống.'; isValid = false;
      } else if (this.form.content.trim().length < 10) {
        this.errors.content = 'Tóm tắt nội dung phải có độ dài tối thiểu 10 ký tự.'; isValid = false;
      }
      if (!this.form.authorIds || this.form.authorIds.length === 0) {
        this.errors.authors = 'Vui lòng chọn ít nhất 1 tác giả.'; isValid = false;
      }
      if (!this.form.categoryIds || this.form.categoryIds.length === 0) {
        this.errors.categories = 'Vui lòng chọn ít nhất 1 thể loại.'; isValid = false;
      }
      if (!this.isEditMode && !this.form.coverFile) {
        toast.error('Vui lòng chọn ảnh bìa cho sách.');
        isValid = false;
      }
      return isValid;
    },

    // ── SAVE ───────────────────────────────────────────────────────────────
    saveBook() {
      if (this.isLoading) return;
      if (!this.validateForm()) return;

      if (this.isEditMode) {
        this.errors.reason = '';
        this.form.reason = '';
        this.reasonModalVisible = true;
        this.$nextTick(() => this.$refs.reasonInput?.focus());
        return;
      }

      this.isLoading = true;
      const formData = new FormData();
      if (this.form.coverFile) {
        formData.append('cover', this.form.coverFile);
      }

      const bookCreateDto = {
        name: this.form.name.trim(),
        content: this.form.content.trim(),
        status: this.form.status,
        linkShopee: this.form.link_shopee.trim(),
        authors: this.form.authorIds,
        categories: this.form.categoryIds,
        translators: this.form.translatorIds
      };
      formData.append('data', JSON.stringify(bookCreateDto));

      bookApi.create(formData)
        .then(() => { toast.success('Thêm sách mới vào kho thành công!'); this.closeModal(); this.reloadData(); })
        .catch(err => toast.error(err.message || 'Có lỗi xảy ra.'))
        .finally(() => { this.isLoading = false; });
    },

    submitUpdateWithReason() {
      if (this.isLoading) return;
      const reasonVal = (this.form.reason || '').trim();
      if (!reasonVal) {
        this.errors.reason = 'Lý do chỉnh sửa không được bỏ trống.';
        return;
      }
      if (reasonVal.length < 10) {
        this.errors.reason = 'Lý do chỉnh sửa phải có ít nhất 10 ký tự.';
        return;
      }
      if (reasonVal.length > 500) {
        this.errors.reason = 'Lý do chỉnh sửa không được quá 500 ký tự.';
        return;
      }

      this.errors.reason = '';
      this.reasonModalVisible = false;
      this.modalVisible = false;
      this.isLoading = true;

      const formData = new FormData();
      if (this.form.coverFile) {
        formData.append('cover', this.form.coverFile);
      }

      const bookUpdateDto = {
        id: this.form.id,
        name: this.form.name.trim(),
        content: this.form.content.trim(),
        status: this.form.status,
        linkShopee: this.form.link_shopee.trim(),
        authors: this.form.authorIds,
        categories: this.form.categoryIds,
        translators: this.form.translatorIds,
        reason: reasonVal,
        updateTime: this.form.updateTime || ''
      };
      formData.append('data', JSON.stringify(bookUpdateDto));

      bookApi.update(formData)
        .then(() => {
          toast.success('Cập nhật thông tin sách thành công!');
          this.reloadData(this.currentPage);
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra.');
          this.reloadData(this.currentPage);
        })
        .finally(() => { this.isLoading = false; });
    },

    closeReasonModal() {
      this.reasonModalVisible = false;
      this.form.reason = '';
      this.errors.reason = '';
    },


    // ── DELETE ─────────────────────────────────────────────────────────────
    confirmDelete(book) {
      this.selectedBook = book;
      this.deleteReason = '';
      this.errors.deleteReason = '';
      this.deleteModalVisible = true;
      this.$nextTick(() => this.$refs.deleteReasonInput?.focus());
    },
    closeDeleteModal() {
      this.deleteModalVisible = false;
      this.selectedBook = null;
      this.deleteReason = '';
      this.errors.deleteReason = '';
    },

    deleteBook() {
      if (!this.selectedBook || this.isLoading) return;
      const reasonVal = (this.deleteReason || '').trim();
      if (!reasonVal) {
        this.errors.deleteReason = 'Lý do xóa không được bỏ trống.';
        return;
      }
      if (reasonVal.length < 10) {
        this.errors.deleteReason = 'Lý do xóa phải có ít nhất 10 ký tự.';
        return;
      }
      if (reasonVal.length > 500) {
        this.errors.deleteReason = 'Lý do xóa không được quá 500 ký tự.';
        return;
      }

      this.errors.deleteReason = '';
      this.isLoading = true;
      const isUserAdmin = this.isAdmin;
      bookApi.remove({
        id: this.selectedBook.id,
        reason: reasonVal,
        updateTime: this.selectedBook.updateTime || this.selectedBook.update_time || ''
      })
        .then((res) => {
          const msg = res?.message || (isUserAdmin
            ? `Đã xóa cuốn sách "${this.selectedBook.name}" thành công!`
            : 'Đã yêu cầu xóa cho cuốn sách này');
          toast.success(msg);
          this.closeDeleteModal();
          this.reloadData(this.currentPage);
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi xóa.');
          this.closeDeleteModal();
          this.reloadData(this.currentPage);
        })
        .finally(() => { this.isLoading = false; });
    },

    // ── RESTORE ────────────────────────────────────────────────────────────
    confirmRestore(book) {
      this.selectedBookForRestore = book;
      this.restoreReason = '';
      this.errors.restoreReason = '';
      this.restoreModalVisible = true;
      this.$nextTick(() => this.$refs.restoreReasonInput?.focus());
    },
    closeRestoreModal() {
      this.restoreModalVisible = false;
      this.selectedBookForRestore = null;
      this.restoreReason = '';
      this.errors.restoreReason = '';
    },
    restoreBook() {
      if (!this.selectedBookForRestore || this.isLoading) return;
      const reasonVal = (this.restoreReason || '').trim();
      const label = this.isAdmin ? 'Lý do khôi phục' : 'Lý do yêu cầu khôi phục';
      if (!reasonVal) {
        this.errors.restoreReason = `${label} không được bỏ trống.`;
        return;
      }
      if (reasonVal.length < 10) {
        this.errors.restoreReason = `${label} phải có ít nhất 10 ký tự.`;
        return;
      }
      if (reasonVal.length > 500) {
        this.errors.restoreReason = `${label} không được quá 500 ký tự.`;
        return;
      }

      this.errors.restoreReason = '';
      this.isLoading = true;
      const isUserAdmin = this.isAdmin;
      const bookName = this.selectedBookForRestore.name;
      bookApi.restore({
        id: this.selectedBookForRestore.id,
        reason: reasonVal,
        updateTime: this.selectedBookForRestore.updateTime || this.selectedBookForRestore.update_time || ''
      })
        .then(() => {
          const msg = isUserAdmin
            ? `Đã khôi phục cuốn sách "${bookName}" thành công!`
            : `Đã gửi yêu cầu khôi phục cuốn sách "${bookName}" thành công!`;
          toast.success(msg);
          this.closeRestoreModal();
          this.reloadData(this.currentPage);
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi khôi phục.');
          this.closeRestoreModal();
          this.reloadData(this.currentPage);
        })
        .finally(() => { this.isLoading = false; });
    },

    // ── HISTORY ────────────────────────────────────────────────────────────
    openHistoryModal(book) {
      this.selectedBook = book;
      this.historyModalVisible = true;
      bookApi.getHistory(book.id)
        .then(res => {
          this.selectedBook = { ...book, history: res };
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi tải lịch sử sách.');
        });
    },
    closeHistoryModal() {
      this.historyModalVisible = false;
      this.selectedBook = null;
    },


    formatTime(ts) {
      if (!ts) return '';
      const str = String(ts).trim();
      if (/^\d{14}$/.test(str)) {
        const [y, m, d, h, min, sec] = [str.slice(0, 4), str.slice(4, 6), str.slice(6, 8), str.slice(8, 10), str.slice(10, 12), str.slice(12, 14)];
        return `${h}:${min}:${sec} ${d}/${m}/${y}`;
      }
      if (/^\d{12}$/.test(str)) {
        const [y, m, d, h, min] = [str.slice(0, 4), str.slice(4, 6), str.slice(6, 8), str.slice(8, 10), str.slice(10, 12)];
        return `${h}:${min}:00 ${d}/${m}/${y}`;
      }
      if (/^\d{2}:\d{2}\s+\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        const parts = str.split(/\s+/);
        return `${parts[0]}:00 ${parts[1]}`;
      }
      return str;
    },
    getHistoryBadgeClass(action) {
      if (action === 'Khởi tạo') return 'badge-init';
      if (action === 'Cập nhật trạng thái') return 'badge-status';
      return 'badge-update';
    },
    getHistoryIcon(action) {
      if (action === 'Khởi tạo') return 'fa-solid fa-plus';
      if (action === 'Cập nhật trạng thái') return 'fa-solid fa-toggle-on';
      return 'fa-solid fa-pen';
    },
    toggleExpand(entryId, field, type) {
      const key = `${entryId}_${field}_${type}`;
      this.expandedStates[key] = !this.expandedStates[key];
    },
    isExpanded(entryId, field, type) {
      const key = `${entryId}_${field}_${type}`;
      return !!this.expandedStates[key];
    },
    truncateText(entryId, field, type, text) {
      if (!text) return '(Trống)';
      const limit = 120;
      if (text.length <= limit) return text;
      const key = `${entryId}_${field}_${type}`;
      if (this.expandedStates[key]) {
        return text;
      }
      return text.substring(0, limit) + '...';
    },
    shouldShowMore(text) {
      return text && text.length > 120;
    },
    translateField(field) {
      switch (field) {
        case 'name': return 'Tên sách';
        case 'content': return 'Tóm tắt nội dung';
        case 'cover': return 'Ảnh bìa';
        case 'linkShopee': return 'Link Shopee Affiliate';
        case 'authors': return 'Tác giả';
        case 'categories': return 'Thể loại';
        case 'translators': return 'Dịch giả';
        default: return field;
      }
    },
    formatRelationNames(jsonIds, field) {
      if (!jsonIds) return '(Trống)';
      try {
        const ids = JSON.parse(jsonIds);
        if (!Array.isArray(ids) || ids.length === 0) return '(Trống)';
        
        let map = {};
        if (field === 'authors') map = this.loadedAuthors;
        else if (field === 'translators') map = this.loadedTranslators;
        else if (field === 'categories') map = this.loadedCategories;
        
        const names = ids.map(id => map[id] || id);
        return names.join(', ');
      } catch (e) {
        return jsonIds;
      }
    },
    onFileChange(e) {
      const file = e.target.files[0];
      this.processFile(file);
    },
    onFileDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files[0];
      this.processFile(file);
    },
    processFile(file) {
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        toast.error('Vui lòng chọn tệp ảnh hợp lệ.');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Kích thước ảnh tối đa là 2MB.');
        return;
      }
      this.form.coverFile = file;
      const reader = new FileReader();
      reader.onload = (event) => {
        this.form.cover = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    removeCover() {
      this.form.cover = '';
      this.form.coverFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    // ── REMOTE SEARCH HELPERS ──────────────────────────────────────────────
    registerAuthors(list) {
      if (!list) return;
      const items = list.content ?? list ?? [];
      items.forEach(item => {
        if (item.id) {
          let label = item.name;
          if (item.yob || item.yod) {
            label += ` (${item.yob || '?'} - ${item.yod || '?'})`;
          }
          this.loadedAuthors[item.id] = label;
          this.loadedAuthorNames[item.id] = item.name;
        }
      });
    },
    registerTranslators(list) {
      if (!list) return;
      const items = list.content ?? list ?? [];
      items.forEach(item => {
        if (item.id) this.loadedTranslators[item.id] = item.name;
      });
    },
    registerCategories(list) {
      if (!list) return;
      const items = list.content ?? list ?? [];
      items.forEach(item => {
        if (item.id) this.loadedCategories[item.id] = item.name;
      });
    },
    deselectAuthor(id) {
      this.form.authorIds = this.form.authorIds.filter(aid => aid !== id);
    },
    deselectTranslator(id) {
      this.form.translatorIds = this.form.translatorIds.filter(tid => tid !== id);
    },
    deselectCategory(id) {
      this.form.categoryIds = this.form.categoryIds.filter(cid => cid !== id);
    },
    fetchRemoteAuthors(query) {
      const q = query.trim();
      if (!q) {
        this.authors = [...this.defaultAuthors];
        return;
      }
      authorApi.search(0, q)
        .then(res => {
          const results = (res.content ?? res ?? []).filter(a => (a.status ?? 0) === 0);
          this.authors = results.slice(0, 10);
          this.registerAuthors(results);
        })
        .catch(err => console.warn('Failed to search authors:', err));
    },
    fetchRemoteTranslators(query) {
      const q = query.trim();
      if (!q) {
        this.translators = [...this.defaultTranslators];
        return;
      }
      translatorApi.search(0, q)
        .then(res => {
          const results = (res.content ?? res ?? []).filter(t => (t.status ?? 0) === 0);
          this.translators = results.slice(0, 10);
          this.registerTranslators(results);
        })
        .catch(err => console.warn('Failed to search translators:', err));
    },
    fetchRemoteCategories(query) {
      const q = query.trim();
      if (!q) {
        this.categories = [...this.defaultCategories];
        return;
      }
      categoryApi.search(q)
        .then(res => {
          const results = (res.content ?? res ?? []).filter(c => (c.status ?? 0) === 0);
          this.categories = results.slice(0, 10);
          this.registerCategories(results);
        })
        .catch(err => console.warn('Failed to search categories:', err));
    },

    isRestoreItem(item) {
      if (!item) return false;
      if (item.status === 3) return true;
      if (item.status !== 1) return false;
      const r = (item.reason || item.requestReason || '').toLowerCase();
      return r.includes('khôi phục');
    },

    getStatusLabel(item) {
      if (!item) return 'Phát hành';
      if (item.status === 2) return 'Đã xóa';
      if (item.status === 3) return 'Chờ khôi phục';
      if (item.status === 1) {
        return this.isRestoreItem(item) ? 'Chờ khôi phục' : 'Chờ xóa';
      }
      return 'Phát hành';
    },

    getStatusTooltip(item) {
      if (!item) return 'Trạng thái: Đang phát hành';
      if (item.status === 2) return 'Trạng thái: Đã xóa';
      if (item.status === 3) return 'Trạng thái: Đang chờ khôi phục';
      if (item.status === 1) {
        return this.isRestoreItem(item) ? 'Trạng thái: Đang chờ khôi phục' : 'Trạng thái: Đang chờ xóa';
      }
      return 'Trạng thái: Đang phát hành';
    },

    getStatusBadgeClass(item) {
      if (!item || item.status === 0 || item.status === undefined || item.status === null) return 'status-badge-pub';
      if (item.status === 3) return 'status-badge-pending-restore';
      if (item.status === 1) return this.isRestoreItem(item) ? 'status-badge-pending-restore' : 'status-badge-pending-del';
      if (item.status === 2) return 'status-badge-deleted';
      return 'status-badge-pub';
    }
  }
};
